import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import ChatContainer from '../components/ChatContainer';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import './ChatPage.css';

const DESKTOP_LAYOUT = '(min-width: 768px) and (min-height: 600px)';

const isMobileLayout = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  !window.matchMedia(DESKTOP_LAYOUT).matches;

export default function ChatPage() {
  const { backendUrl, backendStatus, onRetryConnection, sessionId: currentSessionId, setSessionId: setCurrentSessionId } = useOutletContext();
  const { isAuthenticated } = useAuth();
  // Matches the layout's own desktop rule (see Layout.css): wide AND tall.
  // A landscape phone is >=768px wide but gets the mobile layout, where the
  // history pane is an overlay drawer and must start closed.
  const [historyOpen, setHistoryOpen] = useState(
    () => typeof window !== 'undefined' && !isMobileLayout()
  );
  const [sessions, setSessions] = useState([]);
  const [loadedMessages, setLoadedMessages] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(true);
  // Track session IDs created in this chat so we skip refetching them
  const createdSessionIds = useRef(new Set());

  // Fetch chat sessions on mount (authenticated only — history 401s for guests)
  useEffect(() => {
    if (!isAuthenticated) {
      setSessions([]);
      setLoadingHistory(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/api/history/sessions');
        if (!cancelled) setSessions(data);
      } catch (err) {
        console.error('Failed to load chat sessions', err);
      } finally {
        if (!cancelled) setLoadingHistory(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // When a session is selected from the sidebar, fetch its messages from DB.
  // Skip if the session was just created in this tab (messages already displayed).
  useEffect(() => {
    if (!currentSessionId) { setLoadedMessages(null); return; }
    if (createdSessionIds.current.has(currentSessionId)) return; // already live in ChatContainer
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/api/history/sessions/${currentSessionId}`);
        if (!cancelled) setLoadedMessages(data.messages || []);
      } catch (err) {
        console.error('Failed to load session messages', err);
      }
    })();
    return () => { cancelled = true; };
  }, [currentSessionId]);

  const handleNewChat = useCallback(() => {
    setCurrentSessionId(null);
    setLoadedMessages(null);
    createdSessionIds.current.clear();
  }, [setCurrentSessionId]);

  const handleDeleteSession = useCallback(async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await api.delete(`/api/history/sessions/${id}`);
      setSessions(prev => prev.filter(s => s.id !== id));
      if (currentSessionId === id) {
        setCurrentSessionId(null);
        setLoadedMessages(null);
      }
    } catch (err) {
      console.error('Failed to delete session', err);
    }
  }, [currentSessionId, setCurrentSessionId]);

  // When ChatContainer creates a new session, add it to the sidebar
  const handleSessionCreated = useCallback((newSession) => {
    createdSessionIds.current.add(newSession.id);
    setSessions(prev => [newSession, ...prev]);
  }, []);

  const handleSelectSession = useCallback((id) => {
    createdSessionIds.current.delete(id); // force DB fetch
    setLoadedMessages(null); // clear stale messages while loading
    setCurrentSessionId(id);
    // On mobile the pane covers the conversation it just loaded — get out of
    // the way so the user sees the result of their tap.
    if (isMobileLayout()) setHistoryOpen(false);
  }, [setCurrentSessionId]);

  const handleFavoriteCompound = useCallback((compoundId) => {
    console.log('Save compound:', compoundId);
  }, []);

  const closeHistory = useCallback(() => setHistoryOpen(false), []);

  return (
    <div className="chat-page">
      {isAuthenticated && historyOpen && (
        <button
          type="button"
          className="chat-history-scrim"
          aria-label="Close chat history"
          onClick={closeHistory}
        />
      )}
      {isAuthenticated && (
      <aside className={`chat-history-pane ${historyOpen ? 'open' : ''}`} aria-label="Chat history" aria-hidden={!historyOpen}>
        <div className="chat-history-header">
          <span className="chat-history-title">Chat history</span>
          <div className="chat-history-actions">
            <button type="button" className="new-chat-btn" onClick={handleNewChat} title="New chat">＋</button>
            <button type="button" className="toggle-history" onClick={closeHistory} aria-label="Collapse chat history">◀</button>
          </div>
        </div>
        {loadingHistory ? (
          <div className="history-loading">Loading…</div>
        ) : sessions.length === 0 ? (
          <div className="history-empty">No conversations yet</div>
        ) : (
          <ul className="chat-history-list">
            {sessions.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  className={`history-item ${currentSessionId === s.id ? 'active' : ''}`}
                  onClick={() => handleSelectSession(s.id)}
                >
                  <span className="history-title">{s.title || 'Untitled chat'}</span>
                  <span className="history-preview">{s.preview || ''}</span>
                  <span className="history-meta">{s.updated_at ? new Date(s.updated_at).toLocaleDateString() : ''}</span>
                </button>
                <button
                  type="button"
                  className="history-delete"
                  onClick={(e) => handleDeleteSession(s.id, e)}
                  title="Delete"
                >🗑</button>
              </li>
            ))}
          </ul>
        )}
      </aside>
      )}
      <div className="chat-main">
        <ChatContainer
          backendUrl={backendUrl}
          backendStatus={backendStatus}
          onRetryConnection={onRetryConnection}
          onFavoriteCompound={handleFavoriteCompound}
          sessionId={currentSessionId}
          onSessionChange={setCurrentSessionId}
          loadedMessages={loadedMessages}
          onSessionCreated={handleSessionCreated}
          showHistoryButton={isAuthenticated}
          historyOpen={historyOpen}
          onToggleHistory={() => setHistoryOpen((o) => !o)}
        />
      </div>
    </div>
  );
}
